import axios from "../axiosInstance"
import type { HotlistEntry, FmvReviewEntry, FmvProofFile, DmFmvEntry, CartaExtractionResult, FmvStagingListItem, ClientEmail, ClientEmailAttachment, FmvActivityItem } from "@/types/company"

export const getCompanyInfo = async (companyId: number) => {
  try {
    const res = await axios.get(`/companies/info/${companyId}`)
    const { data } = res
    if (!data.success) {
      throw new Error(data.message || "Failed to fetch company info")
    }

    return data.company
  } catch (error) {
    console.error("Error fetching company info:", error)
    throw error
  }
}

export const createCompany = async (payload: {
  name: string
  liId?: number | null
  liStrId?: string | null
  pbId?: string | null
  cbId?: string | null
}) => {
  const res = await axios.post("/companies", payload)
  const { data } = res
  if (!data.success) {
    throw new Error(data.message || "Failed to create company")
  }
  return data.company
}

export const lookupCompanyIds = async (name: string): Promise<{
  liStrId: string | null
  cbId: string | null
  pbId: string | null
}> => {
  const res = await axios.get("/companies/lookup-ids", { params: { name } })
  const { data } = res
  if (!data.success) {
    throw new Error(data.message || "Failed to lookup company IDs")
  }
  return { liStrId: data.liStrId, cbId: data.cbId, pbId: data.pbId }
}

export type CompanyFile = {
  name: string
  date: string
  type: string
  link: string
}

export const getCompanyFiles = async (companyId: number): Promise<CompanyFile[]> => {
  const res = await axios.get(`/companies/${companyId}/files`)
  const { data } = res
  if (!data.success) {
    throw new Error(data.message || "Failed to fetch company files")
  }
  return data.files
}

export const getFmvTypes = async () => {
  try {
    const res = await axios.get("/companies/fmv-types")
    const { data } = res
    if (!data.success) {
      throw new Error(data.message || "Failed to fetch FMV types")
    }
    return data.fmvTypes as { id: number; name: string | null }[]
  } catch (error) {
    console.error("Error fetching FMV types:", error)
    throw error
  }
}

export const createFmv = async (companyId: number, payload: {
  fmvTypeId: number
  value?: number | null
  comment?: string | null
  name?: string | null
  date: string
}) => {
  const res = await axios.post(`/companies/${companyId}/fmvs`, payload)
  const { data } = res
  if (!data.success) {
    throw new Error(data.message || "Failed to create FMV")
  }
  return data.fmv
}

export const createRumor = async (companyId: number, payload: {
  rumorFlagId?: number | null
  comment?: string | null
  date?: string | null
}) => {
  const res = await axios.post(`/companies/${companyId}/rumors`, payload)
  const { data } = res
  if (!data.success) {
    throw new Error(data.message || "Failed to create rumor")
  }
  return data.rumor
}

// ─── Company Assessment / Scorecard ──────────────────────────────────────────

export interface ScorecardSignal {
  key: string;
  score: number | null;
  rationale: string;
}

export interface ScorecardCategory {
  key: string;
  label: string;
  weight: number;
  signals: ScorecardSignal[];
  categoryScore: number;
  weightedScore: number;
  nullCount: number;
}

export type Tier = "A" | "B" | "C" | "D";

export interface AssessmentScores {
  overallScore: number;
  tier: Tier;
  capTableGateFailed: boolean;
  capTableGateMessage: string | null;
  categories: ScorecardCategory[];
  keyStrengths: ScorecardSignal[];
  keyRisks: ScorecardSignal[];
  isGreat: boolean;
  greatnessReasons: string[];
  dataConfidence: "high" | "medium" | "low";
}

export type AssessmentSignal = "STRONG" | "MODERATE" | "CAUTIOUS" | "AVOID";

export interface CompanyAssessment {
  id: number;
  companyId: number;
  content: string;
  signal: AssessmentSignal;
  scores: AssessmentScores;
  overallScore: number | null;
  tier: string | null;
  capTableGateFailed: boolean;
  investmentOneLiner: string | null;
  inputTokens: number | null;
  outputTokens: number | null;
  createdAt: string;
  updatedAt: string;
  stale: boolean;
  stalenessReason?: string;
}

/** Signal labels for display — mirrors backend SIGNAL_LABELS */
export const SIGNAL_LABELS: Record<string, string> = {
  stage_maturity: "Stage Maturity",
  revenue_scale: "Revenue Scale",
  growth_at_scale: "Growth at Scale",
  liquidity_timeline_visibility: "Liquidity Timeline",
  market_ipo_window: "Market IPO Window",
  liquidation_preference_stack: "LP Stack",
  last_round_valuation_realism: "Valuation Realism",
  down_round_ratchet_history: "Down Round History",
  fmv_vs_preferred_spread: "FMV vs Preferred",
  capital_raised_vs_valuation: "Capital Efficiency",
  fmv_409a_trend: "409A FMV Trend",
  net_revenue_retention: "Net Revenue Retention",
  rule_of_40: "Rule of 40",
  gross_margin: "Gross Margin",
  burn_multiple: "Burn Multiple",
  competitive_moat: "Competitive Moat",
  employee_headcount_trend: "Employee Headcount Trend",
  founder_market_fit: "Founder-Market Fit",
  organic_pull: "Organic Pull",
  employee_confidence_signal: "Employee Confidence",
  market_timing: "Market Timing",
};

export type AssessmentStreamEvent =
  | { type: "status"; text: string }
  | { type: "token"; text: string }
  | { type: "done"; assessment: CompanyAssessment }
  | { type: "error"; message: string };

export const getCompanyAssessment = async (companyId: number): Promise<{
  cached: boolean;
  stale: boolean;
  assessment: CompanyAssessment | null;
  stalenessReason?: string;
}> => {
  const res = await axios.get(`/companies/${companyId}/assessment`)
  return res.data
}

export const runCompanyAssessment = async (
  companyId: number,
  signal: AbortSignal | undefined,
  onEvent: (event: AssessmentStreamEvent) => void
): Promise<CompanyAssessment | null> => {
  const serverUrl = process.env.REACT_APP_SERVER_URL ?? ""
  const res = await fetch(`${serverUrl}/companies/${companyId}/assess`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    signal,
  })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)

  const reader = res.body!.getReader()
  const decoder = new TextDecoder()
  let buffer = ""
  let finalAssessment: CompanyAssessment | null = null

  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    const lines = buffer.split("\n")
    buffer = lines.pop() ?? ""
    for (const line of lines) {
      if (!line.startsWith("data: ")) continue
      try {
        const event = JSON.parse(line.slice(6)) as AssessmentStreamEvent
        onEvent(event)
        if (event.type === "done") finalAssessment = event.assessment
      } catch { /* skip malformed lines */ }
    }
  }
  return finalAssessment
}

export const searchCompaniesByName = async (name: string) => {
  try {
    const res = await axios.get(`/companies/search`, {
      params: { name },
    })
    const { data } = res
    if (!data.success) {
      throw new Error(data.message || "Failed to search companies")
    }

    return data.companies
  } catch (error) {
    console.error("Error searching companies:", error)
    throw error
  }
}

export const upsertCapTableMitigation = async (companyId: number, fundingId: number, memo: string) => {
  const res = await axios.put(`/companies/${companyId}/cap-table-mitigations`, { fundingId, memo })
  if (!res.data.success) throw new Error("Failed to save mitigation")
  return res.data.mitigation
}

export const deleteCapTableMitigation = async (companyId: number, fundingId: number) => {
  const res = await axios.delete(`/companies/${companyId}/cap-table-mitigations/${fundingId}`)
  if (!res.data.success) throw new Error("Failed to remove mitigation")
}

export const getActiveHotlist = async (): Promise<HotlistEntry[]> => {
  const res = await axios.get("/hotlist")
  const { data } = res
  if (!data.success) {
    throw new Error("Failed to fetch hotlist")
  }
  return data.entries
}

export const getFmvReview = async (viewAs?: string): Promise<FmvReviewEntry[]> => {
  const params = viewAs ? { viewAs } : {}
  const res = await axios.get("/fmv-review", { params })
  const { data } = res
  if (!data.success) {
    throw new Error("Failed to fetch FMV review data")
  }
  return data.entries
}

export const getFmvProofFiles = async (folderPath: string): Promise<FmvProofFile[]> => {
  const res = await axios.get("/fmv-review/proof-files", { params: { folderPath } })
  const { data } = res
  if (!data.success) {
    throw new Error("Failed to fetch proof files")
  }
  return data.files
}

export type ProofStatusEntry = {
  companyId: number
  companyName: string
  companyFolderPath: string | null
  latestFmvDate: string
  latestFmv: number | null
  latestProofDate: string | null
  fmvAddedBy: string | null
  fmvAddedByEmail: string | null
  proofUploadDm: { id: number; name: string; shortName: string; email: string }
  checkedAt: string
}

export const getProofStatuses = async (
  viewAs?: string
): Promise<ProofStatusEntry[]> => {
  const params: Record<string, string> = {}
  if (viewAs) params.viewAs = viewAs
  const res = await axios.get("/fmv-review/proof-statuses", { params })
  const { data } = res
  if (!data.success) {
    throw new Error("Failed to fetch proof statuses")
  }
  return data.statuses
}

export const syncProofStatuses = async (): Promise<{
  checked: number
  missing: number
  found: number
  skipped: number
}> => {
  const res = await axios.post("/fmv-review/proof-statuses/sync")
  const { data } = res
  if (!data.success) {
    throw new Error("Failed to sync proof statuses")
  }
  return data
}

export const recheckProofStatus = async (
  companyId: number
): Promise<{ proofFound: boolean; error?: string }> => {
  const res = await axios.post(`/fmv-review/proof-statuses/recheck/${companyId}`)
  const { data } = res
  if (!data.success) {
    throw new Error("Failed to recheck proof status")
  }
  return data
}

export const getFmvProgress = async (viewAs?: string): Promise<{ total: number; updated: number; quarterLabel: string }> => {
  const params = viewAs ? { viewAs } : {}
  const res = await axios.get("/fmv-review/progress", { params })
  return res.data
}

export const getFmvManagers = async (): Promise<FmvManager[]> => {
  const res = await axios.get("/fmv-review/managers")
  return res.data.managers
}

export type FmvManager = {
  id: number
  name: string
  shortName: string
  email: string
  roles: string[]
  isFmvReviewer: boolean
  roleAssignments: { role: { name: string } }[]
}

export const getDmFmvReview = async (viewAs?: string, includeTest?: boolean): Promise<DmFmvEntry[]> => {
  const params: Record<string, string> = {}
  if (viewAs) params.viewAs = viewAs
  if (includeTest) params.includeTest = "true"
  const res = await axios.get("/fmv-review/dm-view", { params })
  if (!res.data.success) {
    throw new Error("Failed to fetch DM FMV review data")
  }
  return res.data.entries
}

export const getClientEmails = async (clientEmail: string): Promise<{ emails: ClientEmail[]; microsoftConnected: boolean }> => {
  const res = await axios.get("/fmv-review/client-emails", { params: { clientEmail } })
  return res.data
}

export const getClientEmailAttachments = async (messageId: string): Promise<ClientEmailAttachment[]> => {
  const res = await axios.get("/fmv-review/client-email-attachments", { params: { messageId } })
  return res.data.attachments
}

export const uploadAttachmentToDropbox = async (payload: {
  contentBytes: string
  fileName: string
  folderPath: string
  companyName?: string
}): Promise<{ success: boolean; dropboxPath: string }> => {
  const res = await axios.post("/fmv-review/upload-attachment", payload)
  return res.data
}

export const cartaExtract = async (cartaAccessId: number): Promise<CartaExtractionResult> => {
  const res = await axios.post("/fmv-review/carta-extract", { cartaAccessId })
  return res.data
}

export const cartaConfirm = async (payload: {
  cartaAccessId: number
  companyId: number
  value: number
  date: string
  screenshotBase64?: string
  folderPath?: string | null
  clientCartaName?: string
}): Promise<{ success: boolean; fmvId?: number; dropboxPath?: string }> => {
  const res = await axios.post("/fmv-review/carta-confirm", payload)
  return res.data
}

export const cartaCloseBrowser = async (): Promise<void> => {
  await axios.post("/fmv-review/carta-close")
}

// ─── FMV Outreach ───────────────────────────────────────────────────────────────

export const previewFmvOutreach = async (
  clientId: number,
  companyId: number,
  viewAs?: string,
): Promise<{ toEmail: string; subject: string; bodyHtml: string; bodyText: string; clientFirstName: string; companyName: string; dmName: string }> => {
  const params = viewAs ? { viewAs } : {}
  const res = await axios.post("/fmv-review/outreach/preview", { clientId, companyId }, { params })
  return res.data
}

export const sendFmvOutreach = async (
  clientId: number,
  companyId: number,
  viewAs?: string,
  overrides?: { toEmail?: string; subject?: string; bodyHtml?: string },
): Promise<void> => {
  const params = viewAs ? { viewAs } : {}
  await axios.post("/fmv-review/outreach/send", { clientId, companyId, overrides }, { params })
}

export const sendBulkFmvOutreach = async (
  targets: { clientId: number; companyId: number }[],
  viewAs?: string,
  templateOverrides?: { toEmail?: string; subjectTemplate?: string; bodyTemplate?: string },
): Promise<{ sent: number; skipped: number; errors: string[] }> => {
  const params = viewAs ? { viewAs } : {}
  const res = await axios.post("/fmv-review/outreach/send-bulk", { targets, templateOverrides }, { params })
  return res.data
}

export const setDoNotContact = async (
  clientId: number,
  companyId: number,
  note?: string,
  viewAs?: string,
): Promise<void> => {
  const params = viewAs ? { viewAs } : {}
  await axios.post("/fmv-review/outreach/do-not-contact", { clientId, companyId, note }, { params })
}

export const blockCompany = async (
  companyId: number,
  note?: string,
  viewAs?: string,
): Promise<void> => {
  const params = viewAs ? { viewAs } : {}
  await axios.post("/fmv-review/outreach/block-company", { companyId, note }, { params })
}

export const unblockCompany = async (
  companyId: number,
  viewAs?: string,
): Promise<void> => {
  const params = viewAs ? { viewAs } : {}
  await axios.post("/fmv-review/outreach/unblock-company", { companyId }, { params })
}

export const clearDoNotContact = async (
  clientId: number,
  companyId: number,
): Promise<void> => {
  await axios.delete("/fmv-review/outreach/do-not-contact", { data: { clientId, companyId } })
}

export const markOutreachStatus = async (
  clientId: number,
  companyId: number,
  status: "contacted" | "not_contacted",
  viewAs?: string,
): Promise<void> => {
  const params = viewAs ? { viewAs } : {}
  await axios.post("/fmv-review/outreach/mark-status", { clientId, companyId, status }, { params })
}

export const getOutreachStatuses = async (
  companyIds: number[],
  viewAs?: string,
): Promise<Record<number, Record<number, import("../../types/company").FmvOutreachStatus>>> => {
  const params: any = { companyIds: companyIds.join(",") }
  if (viewAs) params.viewAs = viewAs
  const res = await axios.get("/fmv-review/outreach/statuses", { params })
  return res.data.statuses
}

export const pollOutreachReplies = async (viewAs?: string): Promise<{ total: number; staged: number }> => {
  const params = viewAs ? { viewAs } : {}
  const res = await axios.post("/fmv-review/outreach/poll", {}, { params })
  return res.data
}

export const saveDmFmvNote = async (
  clientId: number,
  companyId: number,
  note: string,
  viewAs?: string,
): Promise<void> => {
  const params = viewAs ? { viewAs } : {}
  await axios.post("/fmv-review/dm-note", { clientId, companyId, note }, { params })
}

// ─── FMV Fix + Punchlist ──────────────────────────────────────────────────────

export const applyFmvFix = async (
  companyId: number,
  fixOption: string,
  payload: {
    irrTarget?: number
    fmvValue?: number
    fmvName?: string
    fmvComment?: string
    fmvDate?: string
    proofFileName?: string
  },
): Promise<void> => {
  const res = await axios.post("/fmv-review/apply-fix", { companyId, fixOption, ...payload })
  if (!res.data.success) throw new Error(res.data.error || "Failed to apply fix")
}

export type FmvPunchlistItemData = {
  id: number
  companyId: number
  suggestedIrrFmv: number
  current409aValue: number | null
  current409aDate: string | null
  proofFileName: string | null
  status: string
  completedAt: string | null
  createdAt: string
  company: { name: string }
  createdBy: { name: string; shortName: string }
  completedBy: { name: string; shortName: string } | null
}

export const getFmvPunchlist = async (): Promise<{
  pending: FmvPunchlistItemData[]
  completed: FmvPunchlistItemData[]
}> => {
  const res = await axios.get("/fmv-review/punchlist")
  return res.data
}

export const completePunchlistItem = async (id: number): Promise<void> => {
  await axios.post(`/fmv-review/punchlist/${id}/complete`)
}

export const reopenPunchlistItem = async (id: number): Promise<void> => {
  await axios.post(`/fmv-review/punchlist/${id}/reopen`)
}

export const resyncIrrFmv = async (): Promise<{ updated: number; total: number }> => {
  const res = await axios.post("/fmv-review/resync-irr")
  return res.data
}

export const sendProofReminder = async (payload: {
  dmEmail: string
  dmName: string
  companyName: string
  fmvDate?: string | null
  fmvValue?: number | null
  clientName?: string | null
}): Promise<void> => {
  await axios.post("/fmv-review/proof-reminder", payload)
}

// ─── FMV Staging ─────────────────────────────────────────────────────────────────

export const getFmvStagingList = async (viewAs?: string): Promise<FmvStagingListItem[]> => {
  const params = viewAs ? { viewAs } : {}
  const res = await axios.get("/fmv-staging", { params })
  return res.data.records
}

export const stageAttachmentAs409A = async (payload: {
  companyId: number
  companyName?: string | null
  folderPath?: string | null
  contentBytes: string
  fileName: string
  mimeType?: string
  clientName?: string | null
}): Promise<{ success: boolean; stagingId: number; fmvValue: number | null; fmvDate: string | null }> => {
  const res = await axios.post("/fmv-staging/stage-attachment", payload)
  return res.data
}

export const uploadEmailToDropbox = async (payload: {
  messageId: string
  folderPath: string
  fileName: string
  companyName?: string
}): Promise<{ success: boolean; dropboxPath: string }> => {
  const res = await axios.post("/fmv-review/upload-email", payload)
  return res.data
}

export const stageEmailAs409A = async (payload: {
  messageId: string
  companyId: number
  companyName?: string | null
  folderPath?: string | null
  clientName?: string | null
  subject?: string | null
  senderEmail?: string | null
}): Promise<{ success: boolean; stagingId: number; fmvValue: number | null; fmvDate: string | null }> => {
  const res = await axios.post("/fmv-staging/stage-email", payload)
  return res.data
}

// ─── FMV Activity Log ─────────────────────────────────────────────────────────

export const getFmvActivity = async (
  viewAs?: string,
): Promise<{ items: FmvActivityItem[]; lastSeenAt: string | null }> => {
  const params = viewAs ? { viewAs } : {}
  const res = await axios.get("/fmv-review/activity", { params })
  return { items: res.data.items, lastSeenAt: res.data.lastSeenAt }
}

export const getFmvActivityUnseenCount = async (
  viewAs?: string,
): Promise<number> => {
  const params = viewAs ? { viewAs } : {}
  const res = await axios.get("/fmv-review/activity/unseen-count", { params })
  return res.data.count
}

export const markFmvActivitySeen = async (): Promise<void> => {
  await axios.post("/fmv-review/activity/mark-seen")
}

export const correctFmvDate = async (
  companyId: number,
  fmvId: number,
  newDate: string,
  proofFileName?: string,
): Promise<void> => {
  const res = await axios.post("/fmv-review/correct-fmv-date", {
    companyId, fmvId, newDate, proofFileName,
  })
  if (!res.data.success) throw new Error(res.data.error || "Failed to correct FMV date")
}

// ─── FMV Change Report ──────────────────────────────────────────────────────────

export type FmvChangeReportEntry = {
  companyId: number
  companyName: string
  startFmv: number | null
  startFmvDate: string | null
  endFmv: number | null
  endFmvDate: string | null
  percentChange: number | null
  totalInvested: number
}

export const getFmvChangeReport = async (
  startDate: string,
  endDate: string,
): Promise<{ entries: FmvChangeReportEntry[]; startDate: string; endDate: string }> => {
  const res = await axios.get("/fmv-review/change-report", {
    params: { startDate, endDate },
  })
  if (!res.data.success) throw new Error("Failed to fetch FMV change report")
  return { entries: res.data.entries, startDate: res.data.startDate, endDate: res.data.endDate }
}
